import { FC, Fragment } from 'react';

import { Icon } from '@purpur/library/icon';
import { checkmark } from '@purpur/library/icon/assets/checkmark';
import { minus } from '@purpur/library/icon/assets/minus';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';

import { ProductCompareClassification, ProductCompareValue } from '../../../models';
import {
    ClassificationNameCell,
    ClassificationSection,
    FeatureNameCell,
    FeatureRow,
    FeatureValueCell,
    MobileFeatureNameRow,
} from './ComparisonTable.styles';

const FeatureValue: FC<{ value: ProductCompareValue }> = ({ value }) => {
    const { t } = useTranslations();

    if (value === true) {
        return (
            <span role="img" aria-label={t['eshop.comparison.feature.checkmark_alt']}>
                <Icon svg={checkmark} aria-hidden="true" size="xs" />
            </span>
        );
    }
    if (value === false) {
        return (
            <span role="img" aria-label={t['eshop.comparison.feature.minus_alt']}>
                <Icon svg={minus} size="xs" aria-hidden="true" />
            </span>
        );
    }
    if (Array.isArray(value)) {
        return (
            <>
                {value.map((item, index) => (
                    <Fragment key={index}>
                        <FeatureValue value={item} />
                        <br />
                    </Fragment>
                ))}
            </>
        );
    }

    return <>{value}</>;
};

type Props = {
    classification: ProductCompareClassification;
    productsLimit: number;
    productCodes: string[];
};

export const ComparisonRowsSection: FC<Props> = ({ classification, productsLimit, productCodes }) => {
    return (
        <ClassificationSection>
            <tr>
                <ClassificationNameCell scope="rowgroup" colSpan={productsLimit}>
                    {classification.name}
                </ClassificationNameCell>
            </tr>
            {classification.features.map((feature, featureIndex) => (
                <Fragment key={feature.code}>
                    <MobileFeatureNameRow $isEven={featureIndex % 2 === 0}>
                        <th colSpan={productsLimit}>{feature.name}</th>
                    </MobileFeatureNameRow>
                    <FeatureRow $isEven={featureIndex % 2 === 0}>
                        <FeatureNameCell scope="row">{feature.name}</FeatureNameCell>
                        {productCodes.map((productCode) => (
                            <FeatureValueCell key={`${feature.code}-${productCode}`}>
                                <FeatureValue value={feature.values[productCode]} />
                            </FeatureValueCell>
                        ))}
                        {productCodes.length < productsLimit &&
                            Array(productsLimit - Object.keys(feature.values).length)
                                .fill(undefined)
                                .map((_, index) => <FeatureValueCell key={`placeholder-${feature.code}-${index}`} />)}
                    </FeatureRow>
                </Fragment>
            ))}
        </ClassificationSection>
    );
};
